import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Users } from 'lucide-react';
import ContactFormModal from '../../components/ContactFormModal';
import { fetchHomeGpuPricing, type HomeGpuPricingEntry } from '../../utils/pricing';

const CLOUD_URL = import.meta.env.VITE_CLOUD_URL || 'https://cloud.etherworks.cn';

function formatPrice(price: number) {
  return `$${price.toFixed(2)}`;
}

export default function GpuPlans() {
  const { t } = useTranslation('products');
  const [showContact, setShowContact] = useState(false);
  const [enabledGpus, setEnabledGpus] = useState<HomeGpuPricingEntry[]>([]);

  useEffect(() => {
    const loadPricing = async () => {
      const gpus = await fetchHomeGpuPricing();
      setEnabledGpus(gpus.filter((gpu) => gpu.enable).sort((a, b) => b.securePrice - a.securePrice));
    };

    void loadPricing();
  }, []);

  return (
    <>
      {/* GPU Grid */}
      <section className="pt-4 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {enabledGpus.map((gpu) => (
              <div key={gpu.id} className="rounded-xl border border-border bg-card p-5 hover:border-primary/50 transition-all hover:shadow-md group">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-lg font-bold text-foreground leading-tight">{gpu.displayName}</h3>
                </div>
                <div className="text-xs text-muted-foreground space-y-1.5 mb-4">
                  <div className="flex justify-between">
                    <span>{t('gpu_cloud.vram')}</span>
                    <span className="font-medium text-foreground">{gpu.memoryInGb} GB</span>
                  </div>
                </div>
                <div className="pt-3 border-t border-border">
                  <div className="flex items-baseline gap-1">
                    <span className="text-2xl font-bold text-primary">{formatPrice(gpu.securePrice)}</span>
                    <span className="text-xs text-muted-foreground">{t('gpu_cloud.per_hour')}</span>
                  </div>
                  {gpu.communityPrice !== null && gpu.communityPrice > 0 && (
                    <div className="mt-1 text-xs text-muted-foreground">
                      {t('gpu_cloud.community')}: {formatPrice(gpu.communityPrice)}{t('gpu_cloud.per_hour')}
                    </div>
                  )}
                </div>
                <a
                  href={`${CLOUD_URL}/console/compute/pods`}
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                >
                  {t('gpu_cloud.deploy')}
                  <ArrowRight className="w-3.5 h-3.5" />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enterprise CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-muted/30">
        <div className="max-w-3xl mx-auto text-center">
          <Users className="w-10 h-10 text-primary mx-auto mb-4" />
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tighter mb-3">{t('gpu_cloud.enterprise_title')}</h2>
          <p className="text-muted-foreground mb-8">{t('gpu_cloud.enterprise_subtitle')}</p>
          <button
            onClick={() => setShowContact(true)}
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-cyan-500 to-sky-600 hover:from-cyan-600 hover:to-sky-700 text-white font-semibold rounded-xl transition-all shadow-lg shadow-cyan-500/25"
          >
            {t('gpu_cloud.contact_sales')}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {showContact && <ContactFormModal isOpen={showContact} onClose={() => setShowContact(false)} product="gpu-cloud" />}
    </>
  );
}
